/**
 * DataGrid — the shared, advanced data table for every CRM / list view.
 *
 * A single dataset renders two ways:
 *   - `md`+ : a real `<table>` inside its own `overflow-x-auto` scroller, with a
 *     styled **sticky header** (accent underline, subtle depth, hover), sortable
 *     columns (aria-sort, keyboard), a **filter row** (text / select / number /
 *     date-range per column), a **global search** box, a **"Colonnes"** menu
 *     (show / hide + reorder), a **density** toggle, row **selection** (checkbox
 *     + select-all) and **pagination** (client-side, or server-driven via
 *     `serverPagination`).
 *   - below `md` : each row collapses into a token-themed `Card` of
 *     `label: value` pairs, so there is no horizontal scroll on phones. Pass
 *     `renderCard` to override the mobile card.
 *
 * All state (sort / filters / search / hidden columns / order / density / page)
 * lives inside the component; only `selection` stays controlled (so a page's
 * store keeps owning the selected ids). Set `storageKey` to persist the column
 * layout + density + page-size across reloads.
 *
 * No external libraries — pure React + the house token kit (`bg-surface`,
 * `text-muted`, `rounded-token-lg`, `shadow-elevation-1`, `accent`, `primary`,
 * `ring`), so it is theme-aware (light / dark + accent themes) for free.
 */

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { Card } from './Card';
import type { Column, TableSelection } from './ResponsiveTable';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type SortDirection = 'asc' | 'desc';
export type Density = 'comfortable' | 'compact';
export type ColumnFilterKind = 'text' | 'select' | 'number' | 'date';

/** A comparable / filterable scalar produced by a column's `value` accessor. */
export type CellValue = string | number | boolean | Date | null | undefined;

export interface DataGridColumn<T> extends Column<T> {
  /** Enable click-to-sort on this column's header. */
  sortable?: boolean;
  /**
   * Raw comparable / filterable value for the row (used by sort, search and
   * column filters). Falls back to `row[key]` when omitted.
   */
  value?: (row: T) => CellValue;
  /** Per-column filter control. Omit to disable filtering for the column. */
  filter?: ColumnFilterKind;
  /** Options for a `select` filter. Auto-derived from the data when omitted. */
  filterOptions?: { value: string; label: ReactNode }[];
  /** Keep the column out of the "Colonnes" menu (always visible, never moved). */
  locked?: boolean;
  /** Start hidden (still toggleable in the "Colonnes" menu). */
  defaultHidden?: boolean;
  /** Short accessible label used when `header` is not a string. */
  name?: string;
  /** Exclude the column from the global search scan. */
  excludeFromSearch?: boolean;
}

export interface DataGridServerPagination {
  /** Total rows on the server (across all pages). */
  total: number;
  /** Rows per page. */
  pageSize: number;
  /** Current 0-based page index. */
  pageIndex: number;
  /** Move to another page. */
  onPageChange: (pageIndex: number) => void;
  /** Change the page size (renders the size selector when provided). */
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  /** Disables the pager buttons while a fetch is in flight. */
  loading?: boolean;
}

export interface DataGridProps<T> {
  columns: DataGridColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Controlled row selection (checkbox column + select-all). */
  selection?: TableSelection<T>;
  /** Extra toolbar content (e.g. page-specific filters), placed after search. */
  toolbar?: ReactNode;
  /** Show the global search box. Default true. */
  globalSearch?: boolean;
  globalSearchPlaceholder?: string;
  /** Show the "Colonnes" show/hide + reorder menu. Default true. */
  columnManager?: boolean;
  /** Show the density toggle. Default true. */
  densityToggle?: boolean;
  defaultDensity?: Density;
  /** Client-side pagination (ignored when `serverPagination` is set). Default true. */
  paginated?: boolean;
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  /** Server-driven pagination — `rows` are already the current page's slice. */
  serverPagination?: DataGridServerPagination;
  /** Loading / error states (honest, non-blocking). */
  loading?: boolean;
  error?: ReactNode;
  onRetry?: () => void;
  emptyLabel?: ReactNode;
  /** Persist column layout + density + page size under this key. */
  storageKey?: string;
  /** Override the mobile card renderer. */
  renderCard?: (row: T, ctx: { selected: boolean; toggle: () => void }) => ReactNode;
  className?: string;
}

// ---------------------------------------------------------------------------
// Constants / helpers
// ---------------------------------------------------------------------------

const ALIGN: Record<NonNullable<Column<unknown>['align']>, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

const DENSITY_CELL: Record<Density, string> = {
  comfortable: 'px-4 py-3',
  compact: 'px-3 py-1.5',
};
const DENSITY_HEAD: Record<Density, string> = {
  comfortable: 'px-4 py-3',
  compact: 'px-3 py-2',
};

const DEFAULT_PAGE_SIZES = [10, 25, 50, 100];

function colName<T>(column: DataGridColumn<T>): string {
  if (column.name) return column.name;
  if (typeof column.header === 'string') return column.header;
  return column.key;
}

function rawValue<T>(column: DataGridColumn<T>, row: T): CellValue {
  if (column.value) return column.value(row);
  const raw = (row as Record<string, unknown>)[column.key];
  return raw as CellValue;
}

/** A stable, lowercased string form of a cell value for search / text filters. */
function searchText(value: CellValue): string {
  if (value == null) return '';
  if (value instanceof Date) return value.toISOString().toLowerCase();
  return String(value).toLowerCase();
}

function compareValues(a: CellValue, b: CellValue): number {
  // Nullish sinks to the bottom regardless of direction handling upstream.
  const aNull = a == null || a === '';
  const bNull = b == null || b === '';
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (a instanceof Date || b instanceof Date) {
    return new Date(a as string).getTime() - new Date(b as string).getTime();
  }
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return a === b ? 0 : a ? 1 : -1;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function cellDisplay<T>(column: DataGridColumn<T>, row: T): ReactNode {
  if (column.render) return column.render(row);
  const raw = rawValue(column, row);
  return raw == null ? '' : String(raw);
}

interface ColumnFilterState {
  text?: string;
  select?: string;
  min?: string;
  max?: string;
  from?: string;
  to?: string;
}

interface GridPrefs {
  order?: string[];
  hidden?: string[];
  density?: Density;
  pageSize?: number;
}

function loadPrefs(key: string | undefined): GridPrefs {
  if (!key || typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(`datagrid:${key}`);
    return raw ? (JSON.parse(raw) as GridPrefs) : {};
  } catch {
    return {};
  }
}

function savePrefs(key: string | undefined, prefs: GridPrefs) {
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`datagrid:${key}`, JSON.stringify(prefs));
  } catch {
    /* storage full / disabled — prefs are a nicety, never fatal */
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DataGrid<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  selection,
  toolbar,
  globalSearch = true,
  globalSearchPlaceholder = 'Rechercher…',
  columnManager = true,
  densityToggle = true,
  defaultDensity = 'comfortable',
  paginated = true,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  defaultPageSize = 25,
  serverPagination,
  loading = false,
  error,
  onRetry,
  emptyLabel = 'Aucun résultat.',
  storageKey,
  renderCard,
  className = '',
}: DataGridProps<T>) {
  const prefs = useMemo(() => loadPrefs(storageKey), [storageKey]);
  const instanceId = useId();

  // --- column layout (order + visibility) --------------------------------
  const columnByKey = useMemo(() => {
    const map = new Map<string, DataGridColumn<T>>();
    for (const c of columns) map.set(c.key, c);
    return map;
  }, [columns]);

  const [order, setOrder] = useState<string[]>(() => {
    const keys = columns.map((c) => c.key);
    const saved = (prefs.order ?? []).filter((k) => keys.includes(k));
    const missing = keys.filter((k) => !saved.includes(k));
    return [...saved, ...missing];
  });
  // Reconcile order when the columns prop changes (keys added / removed).
  useEffect(() => {
    setOrder((prev) => {
      const keys = columns.map((c) => c.key);
      const kept = prev.filter((k) => keys.includes(k));
      const added = keys.filter((k) => !kept.includes(k));
      if (kept.length === prev.length && added.length === 0) return prev;
      return [...kept, ...added];
    });
  }, [columns]);

  const [hidden, setHidden] = useState<Set<string>>(() => {
    if (prefs.hidden) return new Set(prefs.hidden);
    return new Set(columns.filter((c) => c.defaultHidden).map((c) => c.key));
  });

  const [density, setDensity] = useState<Density>(prefs.density ?? defaultDensity);

  const orderedColumns = useMemo(
    () => order.map((k) => columnByKey.get(k)).filter((c): c is DataGridColumn<T> => c != null),
    [order, columnByKey],
  );
  const visibleColumns = useMemo(
    () => orderedColumns.filter((c) => !hidden.has(c.key)),
    [orderedColumns, hidden],
  );

  // --- sort / search / per-column filters --------------------------------
  const [sort, setSort] = useState<{ key: string; dir: SortDirection } | null>(null);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, ColumnFilterState>>({});
  const [showFilters, setShowFilters] = useState(false);

  const setColumnFilter = (key: string, patch: Partial<ColumnFilterState>) =>
    setFilters((prev) => {
      const next = { ...(prev[key] ?? {}), ...patch };
      // Drop empty keys so the "active" detection stays honest.
      for (const k of Object.keys(next) as (keyof ColumnFilterState)[]) {
        if (next[k] == null || next[k] === '') delete next[k];
      }
      const merged = { ...prev, [key]: next };
      if (Object.keys(next).length === 0) delete merged[key];
      return merged;
    });

  // Auto-derived option lists for `select` filters without explicit options.
  const derivedOptions = useMemo(() => {
    const out: Record<string, { value: string; label: ReactNode }[]> = {};
    for (const c of columns) {
      if (c.filter !== 'select' || c.filterOptions) continue;
      const seen = new Set<string>();
      for (const row of rows) {
        const v = rawValue(c, row);
        if (v == null || v === '') continue;
        const s = String(v);
        if (!seen.has(s)) seen.add(s);
      }
      out[c.key] = [...seen]
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map((v) => ({ value: v, label: v }));
    }
    return out;
  }, [columns, rows]);

  const optionsFor = (c: DataGridColumn<T>) => c.filterOptions ?? derivedOptions[c.key] ?? [];

  // --- processing pipeline: filter → search → sort -----------------------
  const filtered = useMemo(() => {
    let out = rows;

    // Per-column filters.
    const active = Object.entries(filters);
    if (active.length > 0) {
      out = out.filter((row) =>
        active.every(([key, state]) => {
          const column = columnByKey.get(key);
          if (!column) return true;
          const value = rawValue(column, row);
          if (state.text != null) {
            if (!searchText(value).includes(state.text.toLowerCase())) return false;
          }
          if (state.select != null) {
            if (String(value ?? '') !== state.select) return false;
          }
          if (state.min != null || state.max != null) {
            const num = typeof value === 'number' ? value : Number(value);
            if (Number.isNaN(num)) return false;
            if (state.min != null && num < Number(state.min)) return false;
            if (state.max != null && num > Number(state.max)) return false;
          }
          if (state.from != null || state.to != null) {
            const t = value ? new Date(value as string).getTime() : NaN;
            if (Number.isNaN(t)) return false;
            if (state.from != null && t < new Date(state.from).getTime()) return false;
            // `to` is inclusive of the whole day.
            if (state.to != null && t > new Date(state.to).getTime() + 86_399_999) return false;
          }
          return true;
        }),
      );
    }

    // Global search across searchable columns.
    const q = search.trim().toLowerCase();
    if (q) {
      const scan = columns.filter((c) => !c.excludeFromSearch);
      out = out.filter((row) =>
        scan.some((c) => searchText(rawValue(c, row)).includes(q)),
      );
    }

    return out;
  }, [rows, filters, search, columns, columnByKey]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const column = columnByKey.get(sort.key);
    if (!column) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    // Stable sort: decorate with the original index.
    return [...filtered]
      .map((row, i) => [row, i] as const)
      .sort((a, b) => {
        const cmp = compareValues(rawValue(column, a[0]), rawValue(column, b[0]));
        return cmp !== 0 ? cmp * dir : a[1] - b[1];
      })
      .map(([row]) => row);
  }, [filtered, sort, columnByKey]);

  // --- pagination --------------------------------------------------------
  const [clientPageSize, setClientPageSize] = useState<number>(prefs.pageSize ?? defaultPageSize);
  const [clientPage, setClientPage] = useState(0);
  const usingServer = !!serverPagination;
  const usingClientPager = paginated && !usingServer;

  // Clamp the client page whenever the filtered set shrinks.
  const clientPageCount = Math.max(1, Math.ceil(sorted.length / clientPageSize));
  useEffect(() => {
    if (usingClientPager && clientPage > clientPageCount - 1) setClientPage(clientPageCount - 1);
  }, [usingClientPager, clientPage, clientPageCount]);
  // Reset to the first page when the filter / search / sort inputs change.
  useEffect(() => {
    setClientPage(0);
  }, [search, filters, sort, clientPageSize]);

  const pageRows = useMemo(() => {
    if (!usingClientPager) return sorted;
    const start = clientPage * clientPageSize;
    return sorted.slice(start, start + clientPageSize);
  }, [usingClientPager, sorted, clientPage, clientPageSize]);

  // --- persistence -------------------------------------------------------
  useEffect(() => {
    savePrefs(storageKey, {
      order,
      hidden: [...hidden],
      density,
      pageSize: clientPageSize,
    });
  }, [storageKey, order, hidden, density, clientPageSize]);

  // --- sort interaction --------------------------------------------------
  const onSort = (key: string) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null; // asc → desc → none
    });
  };

  const ariaSort = (key: string): 'ascending' | 'descending' | 'none' => {
    if (sort?.key !== key) return 'none';
    return sort.dir === 'asc' ? 'ascending' : 'descending';
  };

  // --- column reorder (accessible move, no drag needed) ------------------
  const moveColumn = (key: string, delta: number) =>
    setOrder((prev) => {
      const i = prev.indexOf(key);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const toggleColumn = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const resetAll = () => {
    setSearch('');
    setFilters({});
    setSort(null);
  };

  // --- selection helpers -------------------------------------------------
  const selectedSet =
    selection == null
      ? null
      : selection.selectedKeys instanceof Set
        ? selection.selectedKeys
        : new Set(selection.selectedKeys);
  const isSelected = (key: string) => selectedSet?.has(key) ?? false;
  const allSelected =
    selection?.allSelected ??
    (pageRows.length > 0 && pageRows.every((r) => isSelected(rowKey(r))));

  // --- active-filter summary --------------------------------------------
  const anyColumnFilterable = columns.some((c) => c.filter);
  const activeFilterCount =
    Object.keys(filters).length + (search.trim() ? 1 : 0) + (sort ? 1 : 0);
  const hasActive = Object.keys(filters).length > 0 || search.trim().length > 0 || sort != null;

  const colSpan = visibleColumns.length + (selection ? 1 : 0);
  const stop = (event: { stopPropagation: () => void }) => event.stopPropagation();

  // Range label for the footer.
  const totalRows = usingServer ? serverPagination.total : sorted.length;
  const pageSize = usingServer ? serverPagination.pageSize : clientPageSize;
  const pageIndex = usingServer ? serverPagination.pageIndex : clientPage;
  const pageCount = usingServer
    ? Math.max(1, Math.ceil(serverPagination.total / Math.max(1, serverPagination.pageSize)))
    : clientPageCount;
  const rangeFrom = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  const rangeTo = usingServer
    ? Math.min((pageIndex + 1) * pageSize, totalRows)
    : Math.min(pageIndex * pageSize + pageRows.length, totalRows);
  const showPager = usingServer || usingClientPager;
  const pagerBusy = usingServer ? (serverPagination.loading ?? false) : false;

  const goToPage = (next: number) => {
    const clamped = Math.max(0, Math.min(next, pageCount - 1));
    if (usingServer) serverPagination.onPageChange(clamped);
    else setClientPage(clamped);
  };
  const changePageSize = (size: number) => {
    if (usingServer) serverPagination.onPageSizeChange?.(size);
    else setClientPageSize(size);
  };
  const sizeOptions = usingServer
    ? (serverPagination.pageSizeOptions ?? pageSizeOptions)
    : pageSizeOptions;

  return (
    <div className={className}>
      {/* Toolbar ---------------------------------------------------------- */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {globalSearch && (
          <div className="relative">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
            >
              🔍
            </span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={globalSearchPlaceholder}
              aria-label={globalSearchPlaceholder}
              className="w-56 max-w-full rounded-token border border-border bg-surface py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
        )}

        {toolbar}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {anyColumnFilterable && (
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              aria-pressed={showFilters}
              className={`inline-flex items-center gap-1.5 rounded-token border px-3 py-2 text-sm font-medium transition ${
                showFilters || Object.keys(filters).length > 0
                  ? 'border-primary/40 bg-primary/10 text-primary'
                  : 'border-border bg-surface text-muted hover:bg-surface-2 hover:text-foreground'
              }`}
            >
              <span aria-hidden>⚟</span> Filtres
              {Object.keys(filters).length > 0 && (
                <span className="rounded-full bg-primary px-1.5 text-xs font-bold text-primary-foreground">
                  {Object.keys(filters).length}
                </span>
              )}
            </button>
          )}

          {densityToggle && (
            <div
              className="inline-flex overflow-hidden rounded-token border border-border"
              role="group"
              aria-label="Densité d’affichage"
            >
              <button
                type="button"
                onClick={() => setDensity('comfortable')}
                aria-pressed={density === 'comfortable'}
                title="Confortable"
                className={`px-2.5 py-2 text-sm ${
                  density === 'comfortable'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-surface text-muted hover:bg-surface-2'
                }`}
              >
                <span aria-hidden>≣</span>
                <span className="sr-only">Confortable</span>
              </button>
              <button
                type="button"
                onClick={() => setDensity('compact')}
                aria-pressed={density === 'compact'}
                title="Compact"
                className={`border-l border-border px-2.5 py-2 text-sm ${
                  density === 'compact'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-surface text-muted hover:bg-surface-2'
                }`}
              >
                <span aria-hidden>≡</span>
                <span className="sr-only">Compact</span>
              </button>
            </div>
          )}

          {columnManager && (
            <ColumnsMenu
              columns={orderedColumns}
              hidden={hidden}
              onToggle={toggleColumn}
              onMove={moveColumn}
              idBase={instanceId}
            />
          )}
        </div>
      </div>

      {/* Active-filter chips --------------------------------------------- */}
      {hasActive && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
          {search.trim() && (
            <FilterChip label={`Recherche : « ${search.trim()} »`} onClear={() => setSearch('')} />
          )}
          {Object.entries(filters).map(([key, state]) => {
            const column = columnByKey.get(key);
            if (!column) return null;
            return (
              <FilterChip
                key={key}
                label={`${colName(column)} : ${describeFilter(state)}`}
                onClear={() => setFilters((p) => {
                  const next = { ...p };
                  delete next[key];
                  return next;
                })}
              />
            );
          })}
          {sort && (
            <FilterChip
              label={`Tri : ${colName(columnByKey.get(sort.key) ?? ({ key: sort.key } as DataGridColumn<T>))} ${
                sort.dir === 'asc' ? '↑' : '↓'
              }`}
              onClear={() => setSort(null)}
            />
          )}
          <button
            type="button"
            onClick={resetAll}
            className="ml-1 font-medium text-primary hover:underline"
          >
            Réinitialiser
          </button>
          <span className="sr-only" aria-live="polite">
            {activeFilterCount} filtre(s) actif(s)
          </span>
        </div>
      )}

      {error != null ? (
        <div className="rounded-token-lg border border-danger/40 bg-danger/5 px-4 py-8 text-center">
          <p className="text-sm font-medium text-danger">{error}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-3 inline-flex items-center justify-center rounded-token border border-border bg-surface px-3 py-1.5 text-sm font-medium text-foreground hover:bg-surface-2"
            >
              Réessayer
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop / tablet : real table in its own horizontal scroller */}
          <div className="hidden overflow-hidden rounded-token-lg border border-border bg-surface shadow-elevation-1 md:block">
            <div className="max-h-[70vh] overflow-auto">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 z-20">
                  <tr className="bg-surface-2 text-xs uppercase tracking-wide text-muted shadow-[inset_0_-1px_0_rgb(var(--color-border))]">
                    {selection && (
                      <th scope="col" className={`w-10 ${DENSITY_HEAD[density]}`}>
                        {selection.onToggleAll && (
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={selection.onToggleAll}
                            className="h-4 w-4 rounded border-border accent-primary focus:ring-ring"
                            aria-label="Tout sélectionner"
                          />
                        )}
                      </th>
                    )}
                    {visibleColumns.map((column) => {
                      const isSortCol = sort?.key === column.key;
                      return (
                        <th
                          key={column.key}
                          scope="col"
                          aria-sort={column.sortable ? ariaSort(column.key) : undefined}
                          className={`relative font-semibold ${DENSITY_HEAD[density]} ${
                            ALIGN[column.align ?? 'left']
                          } ${isSortCol ? 'text-primary' : ''}`}
                        >
                          {column.sortable ? (
                            <button
                              type="button"
                              onClick={() => onSort(column.key)}
                              className={`group inline-flex w-full items-center gap-1 font-semibold uppercase tracking-wide hover:text-foreground ${
                                column.align === 'right'
                                  ? 'justify-end'
                                  : column.align === 'center'
                                    ? 'justify-center'
                                    : ''
                              }`}
                              title="Trier"
                            >
                              <span>{column.header}</span>
                              <SortGlyph active={isSortCol} dir={sort?.dir} />
                            </button>
                          ) : (
                            column.header
                          )}
                          {/* accent underline on the active sort column */}
                          {isSortCol && (
                            <span
                              aria-hidden="true"
                              className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-primary to-accent"
                            />
                          )}
                        </th>
                      );
                    })}
                  </tr>

                  {/* Filter row */}
                  {showFilters && anyColumnFilterable && (
                    <tr className="bg-surface-2/60">
                      {selection && <th className="w-10 px-2 py-2" />}
                      {visibleColumns.map((column) => (
                        <th key={column.key} className="px-2 py-2 align-top font-normal normal-case">
                          <ColumnFilterControl
                            column={column}
                            state={filters[column.key] ?? {}}
                            options={optionsFor(column)}
                            onChange={(patch) => setColumnFilter(column.key, patch)}
                          />
                        </th>
                      ))}
                    </tr>
                  )}
                </thead>

                <tbody className="divide-y divide-border">
                  {pageRows.map((row, index) => {
                    const key = rowKey(row);
                    const selected = isSelected(key);
                    return (
                      <tr
                        key={key}
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                        className={`${onRowClick ? 'cursor-pointer' : ''} transition-colors hover:bg-primary/[0.06] ${
                          selected ? 'bg-primary/10' : index % 2 === 1 ? 'bg-surface-2/40' : ''
                        }`}
                      >
                        {selection && (
                          <td className={DENSITY_CELL[density]} onClick={stop}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => selection.onToggle(row)}
                              className="h-4 w-4 rounded border-border accent-primary focus:ring-ring"
                              aria-label={selection.rowLabel?.(row) ?? 'Sélectionner la ligne'}
                            />
                          </td>
                        )}
                        {visibleColumns.map((column) => (
                          <td
                            key={column.key}
                            className={`${DENSITY_CELL[density]} ${ALIGN[column.align ?? 'left']} ${
                              column.className ?? ''
                            }`}
                          >
                            {cellDisplay(column, row)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}

                  {pageRows.length === 0 && (
                    <tr>
                      <td colSpan={colSpan} className="px-4 py-12 text-center text-muted">
                        {loading ? 'Chargement…' : emptyLabel}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile : stacked cards (no horizontal scroll) */}
          <div className="space-y-3 md:hidden">
            {pageRows.map((row) => {
              const key = rowKey(row);
              const selected = isSelected(key);
              const toggle = () => selection?.onToggle(row);
              if (renderCard) return <div key={key}>{renderCard(row, { selected, toggle })}</div>;
              return (
                <Card
                  key={key}
                  interactive={!!onRowClick}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={selected ? 'ring-1 ring-primary' : ''}
                >
                  {selection && (
                    <label
                      className="mb-2 flex items-center gap-2 text-xs font-medium text-muted"
                      onClick={stop}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={toggle}
                        className="h-4 w-4 rounded border-border accent-primary focus:ring-ring"
                        aria-label={selection.rowLabel?.(row) ?? 'Sélectionner la ligne'}
                      />
                      Sélectionner
                    </label>
                  )}
                  <dl className="space-y-1.5">
                    {visibleColumns.map((column) => (
                      <div key={column.key} className="flex items-start justify-between gap-3">
                        <dt className="shrink-0 text-xs uppercase tracking-wide text-muted">
                          {column.header}
                        </dt>
                        <dd className="min-w-0 text-right text-sm text-foreground">
                          {cellDisplay(column, row)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </Card>
              );
            })}
            {pageRows.length === 0 && (
              <div className="rounded-token-lg border border-border bg-surface px-4 py-12 text-center text-sm text-muted">
                {loading ? 'Chargement…' : emptyLabel}
              </div>
            )}
          </div>

          {/* Footer : pagination + counts ---------------------------------- */}
          {showPager && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-muted">
              <div className="flex items-center gap-2">
                <span aria-live="polite">
                  {rangeFrom}–{rangeTo} sur {totalRows}
                </span>
                {(usingServer ? serverPagination.onPageSizeChange != null : true) && (
                  <label className="ml-2 flex items-center gap-1.5">
                    <span className="hidden sm:inline">Par page</span>
                    <select
                      value={pageSize}
                      onChange={(e) => changePageSize(Number(e.target.value))}
                      aria-label="Lignes par page"
                      className="rounded-token border border-border bg-surface px-2 py-1 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring"
                    >
                      {sizeOptions.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <div className="flex items-center gap-1">
                <PagerButton
                  label="Première page"
                  disabled={pageIndex === 0 || pagerBusy}
                  onClick={() => goToPage(0)}
                >
                  «
                </PagerButton>
                <PagerButton
                  label="Page précédente"
                  disabled={pageIndex === 0 || pagerBusy}
                  onClick={() => goToPage(pageIndex - 1)}
                >
                  ‹
                </PagerButton>
                <span className="px-2 tabular-nums">
                  {pageIndex + 1} / {pageCount}
                </span>
                <PagerButton
                  label="Page suivante"
                  disabled={pageIndex >= pageCount - 1 || pagerBusy}
                  onClick={() => goToPage(pageIndex + 1)}
                >
                  ›
                </PagerButton>
                <PagerButton
                  label="Dernière page"
                  disabled={pageIndex >= pageCount - 1 || pagerBusy}
                  onClick={() => goToPage(pageCount - 1)}
                >
                  »
                </PagerButton>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SortGlyph({ active, dir }: { active: boolean; dir?: SortDirection }) {
  return (
    <span
      aria-hidden="true"
      className={`text-[0.65rem] leading-none transition ${
        active ? 'text-primary' : 'text-muted/50 group-hover:text-muted'
      }`}
    >
      {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
    </span>
  );
}

function PagerButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="inline-flex h-8 min-w-8 items-center justify-center rounded-token border border-border bg-surface px-2 text-foreground hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-primary">
      <span className="max-w-[16rem] truncate">{label}</span>
      <button
        type="button"
        onClick={onClear}
        aria-label={`Retirer ${label}`}
        className="rounded-full px-1 leading-none hover:bg-primary/20"
      >
        ×
      </button>
    </span>
  );
}

function describeFilter(state: ColumnFilterState): string {
  if (state.text != null) return `« ${state.text} »`;
  if (state.select != null) return state.select;
  if (state.min != null || state.max != null) {
    return `${state.min ?? '−∞'} – ${state.max ?? '+∞'}`;
  }
  if (state.from != null || state.to != null) {
    return `${state.from ?? '…'} → ${state.to ?? '…'}`;
  }
  return '—';
}

const FILTER_INPUT =
  'w-full rounded-token border border-border bg-surface px-2 py-1 text-xs text-foreground ' +
  'placeholder:text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring';

function ColumnFilterControl<T>({
  column,
  state,
  options,
  onChange,
}: {
  column: DataGridColumn<T>;
  state: ColumnFilterState;
  options: { value: string; label: ReactNode }[];
  onChange: (patch: Partial<ColumnFilterState>) => void;
}) {
  if (!column.filter) return null;
  const label = colName(column);

  if (column.filter === 'select') {
    return (
      <select
        value={state.select ?? ''}
        onChange={(e) => onChange({ select: e.target.value })}
        aria-label={`Filtrer ${label}`}
        className={FILTER_INPUT}
      >
        <option value="">Tous</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {typeof o.label === 'string' ? o.label : o.value}
          </option>
        ))}
      </select>
    );
  }

  if (column.filter === 'number') {
    return (
      <div className="flex items-center gap-1">
        <input
          type="number"
          value={state.min ?? ''}
          onChange={(e) => onChange({ min: e.target.value })}
          placeholder="min"
          aria-label={`${label} minimum`}
          className={FILTER_INPUT}
        />
        <input
          type="number"
          value={state.max ?? ''}
          onChange={(e) => onChange({ max: e.target.value })}
          placeholder="max"
          aria-label={`${label} maximum`}
          className={FILTER_INPUT}
        />
      </div>
    );
  }

  if (column.filter === 'date') {
    return (
      <div className="flex items-center gap-1">
        <input
          type="date"
          value={state.from ?? ''}
          onChange={(e) => onChange({ from: e.target.value })}
          aria-label={`${label} depuis`}
          className={FILTER_INPUT}
        />
        <input
          type="date"
          value={state.to ?? ''}
          onChange={(e) => onChange({ to: e.target.value })}
          aria-label={`${label} jusqu’à`}
          className={FILTER_INPUT}
        />
      </div>
    );
  }

  // text
  return (
    <input
      type="search"
      value={state.text ?? ''}
      onChange={(e) => onChange({ text: e.target.value })}
      placeholder="Contient…"
      aria-label={`Filtrer ${label}`}
      className={FILTER_INPUT}
    />
  );
}

function ColumnsMenu<T>({
  columns,
  hidden,
  onToggle,
  onMove,
  idBase,
}: {
  columns: DataGridColumn<T>[];
  hidden: Set<string>;
  onToggle: (key: string) => void;
  onMove: (key: string, delta: number) => void;
  idBase: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const menuId = `${idBase}-cols`;

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const manageable = columns.filter((c) => !c.locked);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        className={`inline-flex items-center gap-1.5 rounded-token border px-3 py-2 text-sm font-medium transition ${
          open
            ? 'border-primary/40 bg-primary/10 text-primary'
            : 'border-border bg-surface text-muted hover:bg-surface-2 hover:text-foreground'
        }`}
      >
        <span aria-hidden>▦</span> Colonnes
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          className="absolute right-0 z-30 mt-1 max-h-80 w-64 overflow-auto rounded-token-lg border border-border bg-surface p-1.5 shadow-elevation-3"
        >
          <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted">
            Colonnes affichées
          </p>
          {manageable.map((column, index) => {
            const isHidden = hidden.has(column.key);
            return (
              <div
                key={column.key}
                className="flex items-center gap-1 rounded-token px-1 py-0.5 hover:bg-surface-2"
              >
                <label className="flex flex-1 items-center gap-2 px-1 py-1 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={!isHidden}
                    onChange={() => onToggle(column.key)}
                    className="h-4 w-4 rounded border-border accent-primary focus:ring-ring"
                  />
                  <span className="truncate">{colName(column)}</span>
                </label>
                <button
                  type="button"
                  onClick={() => onMove(column.key, -1)}
                  disabled={index === 0}
                  aria-label={`Déplacer ${colName(column)} à gauche`}
                  className="rounded px-1 text-muted hover:bg-surface-2 hover:text-foreground disabled:opacity-30"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => onMove(column.key, 1)}
                  disabled={index === manageable.length - 1}
                  aria-label={`Déplacer ${colName(column)} à droite`}
                  className="rounded px-1 text-muted hover:bg-surface-2 hover:text-foreground disabled:opacity-30"
                >
                  ↓
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
